import axios from "axios";
import { endPoint } from "../config/endPoint";
import { toast } from "react-toastify";
import { reportOnboardingFailure } from "./onboardingTrace";


const baseUrl = process.env.REACT_APP_API_BASE_URL;
export const genericErrorMessage =
    "We couldn't complete this step right now. Please try again. If this continues, contact support.";
let lastShownErrorAt = 0;

const shouldSkipApiErrorLog = (api: string): boolean => {
    const normalizedApi = (api || "").toLowerCase();
    return normalizedApi.includes(endPoint.clientErrorLog.toLowerCase()) || normalizedApi.includes("getgeneralapid");
};

// The job apply pages toast their own message for a 400 or 429 from these, so a generic one would double up.
const showsOwnErrorToast = (api: string, status?: number): boolean =>
    (api === endPoint.submitJob || api === endPoint.submitMicroSiteJob) && (status === 400 || status === 429);

const maybeShowGenericErrorToast = (api: string, status?: number) => {
    if (shouldSkipApiErrorLog(api) || showsOwnErrorToast(api, status)) {
        return;
    }

    const now = Date.now();
    if (now - lastShownErrorAt < 3000) {
        return;
    }

    lastShownErrorAt = now;
    toast.error(genericErrorMessage);
};

const buildFallbackErrorResponse = (error: any) => {
    const statusCode = Number(error?.response?.status || 0) || 0;
    const statusText = error?.response?.statusText || "Request failed";
    return (
        error?.response || {
            status: statusCode,
            statusText,
            data: {
                message: genericErrorMessage,
            },
        }
    );
};

const sanitizeForLog = (value: any) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    const sensitive = ["password", "token", "authorization", "auth", "bearer", "clientpassword", "webhookpassword"];

    const walk = (input: any): any => {
        if (Array.isArray(input)) {
            return input.map(walk);
        }
        if (input && typeof input === "object") {
            const out: Record<string, any> = {};
            Object.keys(input).forEach((key) => {
                const keyLower = key.toLowerCase();
                const isSensitive = sensitive.some((needle) => keyLower.includes(needle));
                out[key] = isSensitive ? "[REDACTED]" : walk((input as any)[key]);
            });
            return out;
        }
        if (typeof input === "string") {
            return input.length > 4000 ? input.slice(0, 4000) : input;
        }
        return input;
    };

    return walk(value);
};

const buildRequestId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;



const logApiError = (api: string, method: string, requestPayload: any, queryParams: any, errorResponse: any) => {
    if (!baseUrl) {
        return;
    }
    if (shouldSkipApiErrorLog(api)) {
        return;
    }

    try {
        const payload = {
            request_id: buildRequestId(),
            client_id: sessionStorage.getItem("clientId") || null,
            method,
            endpoint: api,
            route_name: null,
            status_code: Number(errorResponse?.status || errorResponse?.data?.statusCode || 0) || null,
            success: false,
            error_type: errorResponse?.status ? "http_error" : "network_error",
            error_message: (errorResponse?.data?.message || errorResponse?.statusText || "Request failed"),
            request_payload: sanitizeForLog(requestPayload),
            response_payload: sanitizeForLog(errorResponse?.data || errorResponse),
            query_params: sanitizeForLog(queryParams),
            page_url: typeof window !== "undefined" ? window.location.href : null,
        };

        void axios.post(`${baseUrl}${endPoint.clientErrorLog}`, payload);
    } catch (_error) {
        // intentionally swallowed
    }
};


export async function apiPost(api: string, params: any, urlparams?:any) {
    try {
        const response: any = await axios.post(baseUrl + api + (urlparams? `?${"domainWithHttp=" + urlparams.domainWithHttp}` : ""), params);
        return response;
    } catch (error: any) {
        logApiError(api, "POST", params, null, error?.response || error);
        reportOnboardingFailure(api, "POST", params, error);
        maybeShowGenericErrorToast(api, error?.response?.status);
        return buildFallbackErrorResponse(error);
    }
}

export async function apiGet(api: string, params: any, urlparams?:any) {
    try {
        const response = await axios.get(baseUrl + api + (urlparams? `?${"domainWithHttp=" + urlparams.domainWithHttp}` : ""), {
            params: params
        });
        return response;
    } catch (error: any) {
        logApiError(api, "GET", null, params, error?.response || error);
        reportOnboardingFailure(api, "GET", params, error);
        maybeShowGenericErrorToast(api);
        return buildFallbackErrorResponse(error);
    }

}

export async function apiPut(api: string, params: any) {
    try {
        const response = await axios.put(baseUrl + api, params);
        return response;
    } catch (error: any) {
        logApiError(api, "PUT", params, null, error?.response || error);
        maybeShowGenericErrorToast(api);
        return buildFallbackErrorResponse(error);
    }

}



export async function apiDelete(api: string, params: any) {
    try {
        const response = await axios.delete(baseUrl + api, {
            params: params
        });
        return response;
    } catch (error: any) {
        logApiError(api, "DELETE", null, params, error?.response || error);
        maybeShowGenericErrorToast(api);
        return buildFallbackErrorResponse(error);
    }

}



